import AIChatWidget from '@/components/aichat/AiChatWidget';
import InstituteSocialActions from '@/components/landing/institute-social-actions';
import SeoHead from '@/components/seo/SeoHead';
import LandingLayout from '@/layouts/landing-layout';
import { Link, router } from '@inertiajs/react';
import { ChevronRight, Lightbulb } from 'lucide-react';
import { useState } from 'react';
const baseUrl = (import.meta as any).env?.VITE_APP_URL ?? (typeof window !== 'undefined' ? window.location.origin : '');

type Institute = {
    id: number;
    name: string;
    logo: string | null;
    image: string | null;
    facebook_link?: string | null;
    email?: string | null;
};

type Props = {
    institute: Institute;
    tab: string;
};

type TabKey = 'about' | 'elementary' | 'junior' | 'senior';

const validTabs: TabKey[] = ['about', 'elementary', 'junior', 'senior'];

export default function BasicEducationCenter({ institute, tab }: Props) {
    const initialTab = validTabs.includes(tab as TabKey) ? (tab as TabKey) : 'about';
    const [activeTab, setActiveTab] = useState<TabKey>(initialTab);

    const heroImage = institute.image ? `${baseUrl}/storage/institutes/images/${institute.image}` : null;
    const logoSrc = institute.logo ? `${baseUrl}/storage/institutes/logos/${institute.logo}` : null;
    const seoImage = heroImage || logoSrc || `${baseUrl}/images/logo.webp`;

    const handleChangeTab = (nextTab: TabKey) => {
        setActiveTab(nextTab);
        router.get(
            route('bec'),
            { tab: nextTab },
            {
                preserveScroll: true,
                preserveState: true,
                replace: true,
            },
        );
    };

    return (
        <LandingLayout>
            <SeoHead
                title={institute.name || 'Basic Education Center'}
                description={`Learn about ${institute.name || 'the institute'} at Northeastern College.`}
                image={seoImage}
            />

            <div className="relative min-h-[380px] overflow-hidden bg-black md:min-h-[460px]">
                {heroImage ? (
                    <img
                        src={heroImage}
                        alt={`${institute.name} cover`}
                        className="absolute inset-0 h-full w-full scale-105 object-cover opacity-60"
                    />
                ) : (
                    <img
                        src="/images/hero/nc1.png"
                        alt="Northeastern College campus"
                        className="absolute inset-0 h-full w-full scale-105 object-cover opacity-50"
                    />
                )}

                <div className="absolute inset-0 bg-gradient-to-r from-[#02301c]/95 via-[#02301c]/80 to-transparent" />
                <div className="absolute inset-0 bg-[radial-gradient(circle_at_bottom_left,rgba(10,122,53,0.35),transparent_60%)]" />

                <div className="absolute inset-0 bg-[linear-gradient(to_right,rgba(255,255,255,0.03)_1px,transparent_1px),linear-gradient(to_bottom,rgba(255,255,255,0.03)_1px,transparent_1px)] bg-[size:3rem_3rem] [mask-image:radial-gradient(ellipse_60%_50%_at_50%_0%,#000_70%,transparent_100%)]" />

                <div className="pointer-events-none absolute top-1/4 left-1/4 h-64 w-64 rounded-full bg-emerald-500/10 blur-[100px]" />
                <div className="pointer-events-none absolute right-1/4 bottom-1/4 h-64 w-64 rounded-full bg-emerald-500/5 blur-[100px]" />

                <div className="relative z-10 mx-auto flex min-h-[380px] max-w-7xl flex-col justify-center px-4 py-16 text-white md:min-h-[460px] md:py-24">
                    <div className="mb-6 flex items-center gap-2 text-xs font-semibold tracking-wide text-[#21d36b]/90 uppercase md:text-sm">
                        <Link href="/" prefetch className="transition-colors hover:text-emerald-300">
                            Home
                        </Link>
                        <ChevronRight className="size-4" aria-hidden="true" />
                        <span className="text-white/80">Institutes</span>
                        <ChevronRight className="size-4" aria-hidden="true" />
                        <span className="text-white/80">{institute.name || 'Basic Education Center'}</span>
                    </div>

                    <div className="flex flex-col items-center gap-6 text-center md:flex-row md:text-left">
                        {logoSrc && (
                            <div className="border-border flex h-24 w-24 shrink-0 items-center justify-center overflow-hidden rounded-full border bg-white shadow-lg md:h-28 md:w-28">
                                <img src={logoSrc} alt={`${institute.name} logo`} className="h-[80%] w-[80%] rounded-full object-contain" />
                            </div>
                        )}
                        <div>
                            <h1 className="text-4xl leading-tight font-extrabold tracking-tight text-white md:text-6xl">
                                {institute.name || 'Basic Education Center'}
                            </h1>
                            <InstituteSocialActions
                                email={institute.email}
                                facebookHref={institute.facebook_link}
                                instituteName={institute.name || 'Basic Education Center'}
                            />
                            <p className="mt-4 max-w-3xl text-base leading-relaxed font-normal text-white/85 md:text-lg">
                                Basic Education Programs and Philosophy
                            </p>
                        </div>
                    </div>
                </div>
            </div>

            <section className="bg-muted relative z-20 py-12">
                <div className="mx-auto max-w-7xl px-4">
                    {/* Floating Navigation Card */}
                    <div className="relative z-20 mx-auto -mt-20 mb-10 max-w-7xl">
                        <div className="bg-card border-border flex flex-col items-center justify-center gap-6 rounded-2xl border p-4 shadow-sm md:flex-row md:p-6">
                            <div className="flex flex-wrap justify-center gap-2">
                                <button
                                    type="button"
                                    onClick={() => handleChangeTab('about')}
                                    className={`flex shrink-0 items-center justify-center gap-2 rounded-full px-4 py-2 text-xs font-semibold transition-all duration-200 md:px-5 md:py-2.5 md:text-sm ${
                                        activeTab === 'about'
                                            ? 'bg-[#0A7A35] text-white shadow-md'
                                            : 'text-muted-foreground hover:text-foreground hover:bg-muted'
                                    }`}
                                >
                                    About
                                </button>
                                <button
                                    type="button"
                                    onClick={() => handleChangeTab('elementary')}
                                    className={`flex shrink-0 items-center justify-center gap-2 rounded-full px-4 py-2 text-xs font-semibold transition-all duration-200 md:px-5 md:py-2.5 md:text-sm ${
                                        activeTab === 'elementary'
                                            ? 'bg-[#0A7A35] text-white shadow-md'
                                            : 'text-muted-foreground hover:text-foreground hover:bg-muted'
                                    }`}
                                >
                                    Elementary
                                </button>
                                <button
                                    type="button"
                                    onClick={() => handleChangeTab('junior')}
                                    className={`flex shrink-0 items-center justify-center gap-2 rounded-full px-4 py-2 text-xs font-semibold transition-all duration-200 md:px-5 md:py-2.5 md:text-sm ${
                                        activeTab === 'junior'
                                            ? 'bg-[#0A7A35] text-white shadow-md'
                                            : 'text-muted-foreground hover:text-foreground hover:bg-muted'
                                    }`}
                                >
                                    Junior High
                                </button>
                                <button
                                    type="button"
                                    onClick={() => handleChangeTab('senior')}
                                    className={`flex shrink-0 items-center justify-center gap-2 rounded-full px-4 py-2 text-xs font-semibold transition-all duration-200 md:px-5 md:py-2.5 md:text-sm ${
                                        activeTab === 'senior'
                                            ? 'bg-[#0A7A35] text-white shadow-md'
                                            : 'text-muted-foreground hover:text-foreground hover:bg-muted'
                                    }`}
                                >
                                    Senior High
                                </button>
                            </div>
                        </div>
                    </div>
                    {activeTab === 'about' && (
                        <div className="space-y-8">
                            <div className="bg-card border-border relative space-y-4 overflow-hidden rounded-2xl border p-6 shadow-sm transition-all duration-300 hover:-translate-y-1 hover:border-emerald-500/20 hover:shadow-md md:p-7">
                                <div className="flex h-12 w-12 items-center justify-center rounded-full bg-emerald-500/10 text-emerald-600 dark:text-[#21d36b]">
                                    <Lightbulb className="h-6 w-6" />
                                </div>
                                <h3 className="text-foreground text-lg font-semibold md:text-xl">About</h3>
                                <p className="text-muted-foreground text-sm leading-relaxed md:text-base">
                                    The Basic Education Center (BEC) has three major programs to offer: Elementary, Junior High School, and Senior
                                    High School. The Junior High School is at Level II status accredited by PACUCOA. The Basic Education Center (BEC)
                                    is a training ground of student teachers from the College of Education in Northeastern College. As the College of
                                    Education is one of the pioneering departments offered by Northeastern College since its founding date in 1941,
                                    the BEC was established to offer affordable education and contribute to uplift literacy which will enable
                                    responsible citizens and future leaders in the nation. Nailed by this responsibility, it is significant to
                                    institutionalize the BEC&apos;s existence and operation as one distinct body in the school&apos;s family of
                                    departments, and to it advocates to instill the following.
                                </p>
                            </div>

                            <div className="bg-card border-border relative overflow-hidden rounded-2xl border p-6 shadow-sm transition-all duration-300 hover:-translate-y-1 hover:border-emerald-500/20 hover:shadow-md md:p-7">
                                <h3 className="text-foreground mb-3 text-lg font-semibold md:text-xl">Philosophy</h3>
                                <p className="text-muted-foreground text-sm leading-relaxed md:text-base">
                                    The Northeastern College believes that every individual deserves accessible quality basic education that will
                                    transform them into a better person possessing necessary life skills and values. Thus, it offers learners various
                                    opportunities to discover their abilities and potentials leading towards their holistic growth and development.
                                </p>
                            </div>

                            <div className="bg-card border-border relative space-y-4 overflow-hidden rounded-2xl border p-6 shadow-sm transition-all duration-300 hover:-translate-y-1 hover:border-emerald-500/20 hover:shadow-md md:p-7">
                                <h3 className="text-foreground text-lg font-semibold md:text-xl">Vision & Mission</h3>
                                <div>
                                    <h4 className="text-foreground mb-2 text-base font-semibold md:text-lg">Vision</h4>
                                    <p className="text-muted-foreground text-sm leading-relaxed md:text-base">
                                        Northeastern College Basic Education Center is a private non-sectarian institution for young learners
                                        committed to the education of responsible citizens and future leaders of the nation who are paragons of
                                        excellence in all fields.
                                    </p>
                                </div>
                                <div>
                                    <h4 className="text-foreground mb-2 text-base font-semibold md:text-lg">Mission</h4>
                                    <ul className="text-muted-foreground list-inside list-decimal space-y-2 text-sm leading-relaxed md:text-base">
                                        <li>Contribute to the literacy uplift of the valley.</li>
                                        <li>Build up the social, moral, and spiritual values of its educands.</li>
                                        <li>Produce well-prepared individuals for economic responsibilities.</li>
                                        <li>
                                            Assist the community discover its potential towards the enjoyment of progressive and peaceful life of its
                                            members.
                                        </li>
                                    </ul>
                                </div>
                                <div>
                                    <h4 className="text-foreground mb-2 text-base font-semibold md:text-lg">Core Values</h4>
                                    <ul className="text-muted-foreground list-inside list-disc space-y-2 text-sm leading-relaxed md:text-base">
                                        <li>Nurtured</li>
                                        <li>Competitive</li>
                                    </ul>
                                </div>
                            </div>
                        </div>
                    )}

                    {activeTab === 'elementary' && (
                        <div className="space-y-8">
                            <div className="bg-card border-border relative space-y-4 overflow-hidden rounded-2xl border p-6 shadow-sm transition-all duration-300 hover:-translate-y-1 hover:border-emerald-500/20 hover:shadow-md md:p-7">
                                <h3 className="text-foreground text-lg font-semibold md:text-xl">Vision & Mission</h3>
                                <div>
                                    <h4 className="text-foreground mb-2 text-base font-semibold md:text-lg">Vision</h4>
                                    <p className="text-muted-foreground text-sm leading-relaxed md:text-base">
                                        Engrained in the attainment of producing productive, useful, and responsible children who can stand
                                        contributory to nation building, it is hoped that in the years to come the program will banner itself a true
                                        fountain of wisdom this valley can be proud of.
                                    </p>
                                </div>
                                <div>
                                    <h4 className="text-foreground mb-2 text-base font-semibold md:text-lg">Mission</h4>
                                    <ul className="text-muted-foreground list-inside list-decimal space-y-2 text-sm leading-relaxed md:text-base">
                                        <li>Contribute to the literacy uplift of the valley.</li>
                                        <li>Uphold the holistic growth and development of the learners.</li>
                                        <li>Produce well-adjusted young learners able to cope with the needs and progress of changing society.</li>
                                    </ul>
                                </div>
                                <div>
                                    <h4 className="text-foreground mb-2 text-base font-semibold md:text-lg">Core Values</h4>
                                    <ul className="text-muted-foreground list-inside list-disc space-y-2 text-sm leading-relaxed md:text-base">
                                        <li>Nurtured</li>
                                        <li>Competitive</li>
                                    </ul>
                                </div>
                            </div>

                            <div className="bg-card border-border relative overflow-hidden rounded-2xl border p-6 shadow-sm transition-all duration-300 hover:-translate-y-1 hover:border-emerald-500/20 hover:shadow-md md:p-7">
                                <h3 className="text-foreground mb-3 text-lg font-semibold md:text-xl">Objectives</h3>
                                <ul className="text-muted-foreground list-inside list-decimal space-y-2 text-sm leading-relaxed md:text-base">
                                    <li>
                                        Provide basic knowledge and develop skills, attitudes, and values essential for holistic development of the
                                        child necessary for day to day living and in contributing to the needs and progress of a changing society.
                                    </li>
                                    <li>
                                        Provide varied and rich learning experiences that enhance the child&apos;s potentials, abilities, and talents
                                        for their future life.
                                    </li>
                                    <li>
                                        Foster love of God and country and inculcate respect for the dignity, rights, and freedom of every citizen in
                                        a society.
                                    </li>
                                </ul>
                            </div>
                        </div>
                    )}

                    {activeTab === 'junior' && (
                        <div className="space-y-8">
                            <div className="bg-card border-border relative overflow-hidden rounded-2xl border p-6 shadow-sm transition-all duration-300 hover:-translate-y-1 hover:border-emerald-500/20 hover:shadow-md md:p-7">
                                <h3 className="text-foreground mb-3 text-lg font-semibold md:text-xl">Philosophy</h3>
                                <p className="text-muted-foreground text-sm leading-relaxed md:text-base">
                                    The Northeastern College believes that every individual deserves accessible quality basic education that will
                                    transform them into a better person, possessing necessary life skills and values. Thus, it offers learners various
                                    opportunities to discover their abilities and potentials leading towards their holistic growth and development.
                                </p>
                            </div>

                            <div className="bg-card border-border relative space-y-4 overflow-hidden rounded-2xl border p-6 shadow-sm transition-all duration-300 hover:-translate-y-1 hover:border-emerald-500/20 hover:shadow-md md:p-7">
                                <h3 className="text-foreground text-lg font-semibold md:text-xl">Vision & Mission</h3>
                                <div>
                                    <h4 className="text-foreground mb-2 text-base font-semibold md:text-lg">Vision</h4>
                                    <p className="text-muted-foreground text-sm leading-relaxed md:text-base">
                                        Northeastern College Basic Education Center is a private non-sectarian institution for young learners
                                        committed to the education of responsible citizens and future leaders of the nation who are paragons of
                                        excellence in all fields.
                                    </p>
                                </div>
                                <div>
                                    <h4 className="text-foreground mb-2 text-base font-semibold md:text-lg">Mission</h4>
                                    <ul className="text-muted-foreground list-inside list-decimal space-y-2 text-sm leading-relaxed md:text-base">
                                        <li>Contribute to the literacy uplift of the valley.</li>
                                        <li>Build up the social, moral, and spiritual values of its educands.</li>
                                        <li>Produce well-prepared individuals for economic responsibilities.</li>
                                        <li>
                                            Assist the community discover its potentials towards the enjoyment of progressive and peaceful life of its
                                            members.
                                        </li>
                                    </ul>
                                </div>
                                <div>
                                    <h4 className="text-foreground mb-2 text-base font-semibold md:text-lg">Core Values</h4>
                                    <ul className="text-muted-foreground list-inside list-disc space-y-2 text-sm leading-relaxed md:text-base">
                                        <li>Nurtured</li>
                                        <li>Competitive</li>
                                    </ul>
                                </div>
                            </div>

                            <div className="bg-card border-border relative overflow-hidden rounded-2xl border p-6 shadow-sm transition-all duration-300 hover:-translate-y-1 hover:border-emerald-500/20 hover:shadow-md md:p-7">
                                <h3 className="text-foreground mb-3 text-lg font-semibold md:text-xl">Objectives</h3>
                                <ul className="text-muted-foreground list-inside list-decimal space-y-2 text-sm leading-relaxed md:text-base">
                                    <li>
                                        Provide the adolescent with knowledge and skills to make the transfer from childhood to adulthood more
                                        productive and meaningful akin to the congenial atmosphere in the home.
                                    </li>
                                    <li>
                                        Equip learners with the skills and develop positive attitude in coping with the educational demands by
                                        enhancing emotional quotient and developing a sound and healthy value system.
                                    </li>
                                    <li>
                                        Acquire skills to explore and define their identity as individuals to become academically excellent, morally
                                        upright, and socially responsible individuals of society.
                                    </li>
                                    <li>
                                        Teach students to engage in outreach programs where they can learn valuable lessons on solidarity to create
                                        lifelong impact to them.
                                    </li>
                                    <li>
                                        Define ways to broaden students&apos; interests to enhance their interpersonal relationship and social
                                        effectiveness.
                                    </li>
                                </ul>
                            </div>
                        </div>
                    )}

                    {activeTab === 'senior' && (
                        <div className="space-y-8">
                            <div className="bg-card border-border relative space-y-4 overflow-hidden rounded-2xl border p-6 shadow-sm transition-all duration-300 hover:-translate-y-1 hover:border-emerald-500/20 hover:shadow-md md:p-7">
                                <h3 className="text-foreground text-lg font-semibold md:text-xl">Vision & Mission</h3>
                                <div>
                                    <h4 className="text-foreground mb-2 text-base font-semibold md:text-lg">Vision</h4>
                                    <p className="text-muted-foreground text-sm leading-relaxed md:text-base">
                                        A leading, senior high school driven basic education center in the region.
                                    </p>
                                </div>
                                <div>
                                    <h4 className="text-foreground mb-2 text-base font-semibold md:text-lg">Mission</h4>
                                    <p className="text-muted-foreground text-sm leading-relaxed md:text-base">
                                        Northeastern College Senior High School is committed to provide quality and industry-driven environment for
                                        instruction, work immersion, and social responsibility to develop integral individual leaders and life-long
                                        learners who are globally competitive.
                                    </p>
                                </div>
                                <div>
                                    <h4 className="text-foreground mb-2 text-base font-semibold md:text-lg">Core Values</h4>
                                    <ul className="text-muted-foreground list-inside list-disc space-y-2 text-sm leading-relaxed md:text-base">
                                        <li>Nurtured</li>
                                        <li>Competitive</li>
                                    </ul>
                                </div>
                            </div>

                            <div className="bg-card border-border relative overflow-hidden rounded-2xl border p-6 shadow-sm transition-all duration-300 hover:-translate-y-1 hover:border-emerald-500/20 hover:shadow-md md:p-7">
                                <h3 className="text-foreground mb-3 text-lg font-semibold md:text-xl">Objectives</h3>
                                <ul className="text-muted-foreground list-inside list-decimal space-y-2 text-sm leading-relaxed md:text-base">
                                    <li>Cater qualified students in pursuit of their senior high school.</li>
                                    <li>Ensure students are given appropriate knowledge and work experiences.</li>
                                    <li>Ensure students hurdle the SHS and pursue higher education.</li>
                                    <li>Senior high students can apply their acquired knowledge and lifelong skills both locally and globally.</li>
                                </ul>
                            </div>
                        </div>
                    )}
                </div>
            </section>

            <AIChatWidget />
        </LandingLayout>
    );
}
